import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaKey } from 'react-icons/fa';
import { useSelector } from 'react-redux';

const Verification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const token = useSelector((state) => state.auth.token);
  const email = location.state?.username;

  if (!email) {
    navigate('/signup');
    return null;
  }

  const handleVerification = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/verify`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Api-Name': 'auth-api',
        },
        body: JSON.stringify({ email, code: verificationCode }),
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess('Email verified successfully!');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(data.message || 'Verification failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      // Provide more meaningful error messages based on the error type
      if (error.message === 'Failed to fetch') {
        setError('Unable to connect to the server. Try again later.');
      } else if (error.message.includes('NetworkError') || error.message.includes('Network request failed')) {
        setError('Network error occurred. Please check your connection and try again.');
      } else if (error.message.includes('timeout')) {
        setError('Request timed out. Please try again later.');
      } else if (error.message.includes('code')) {
        setError('Invalid verification code. Please check and try again.');
      } else if (error.message.includes('expired')) {
        setError('Verification code has expired. Please request a new one.');
      } else {
        setError('An error occurred during verification. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-pri-dark to-sec-dark">
      {/* Animated background elements */}
      <motion.div
        className="absolute top-0 left-0 w-96 h-96 bg-pri-yellow rounded-full mix-blend-multiply filter blur-[128px] opacity-10"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
          x: [-50, 0, -50],
          y: [-50, 0, -50],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-96 h-96 bg-pri-green rounded-full mix-blend-multiply filter blur-[128px] opacity-10"
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [90, 0, 90],
          x: [50, 0, 50],
          y: [50, 0, 50],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <div className="relative min-h-screen flex items-center justify-center py-2 sm:py-8 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-6 sm:space-y-8 -mt-8 sm:-mt-4"
        >
          {/* Logo and Title */}
          <div className="text-center">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mx-auto w-16 sm:w-20 h-16 sm:h-20 relative"
            >
              <img 
                className="h-16 w-auto mx-auto" 
                src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`} 
                alt="Jingleez" 
              />
              <motion.div
                className="absolute inset-0 bg-soft-pink rounded-full opacity-20 blur-md"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.2, 0.3, 0.2],
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
            <h2 className="mt-6 text-3xl sm:text-4xl font-extrabold text-soft-pink">
              Verify Your Email
            </h2>
            <p className="mt-2 text-sm text-pri-gray">
              Please enter the verification code sent to<br />
              <span className="text-soft-pink font-medium">{email}</span>
            </p>
          </div>

          {/* Form Container */}
          <motion.div
            className="relative overflow-hidden rounded-2xl p-8 
                      bg-gradient-to-br from-soft-pink/10 to-soft-pink/5 
                      backdrop-blur-sm shadow-lg border border-white/10
                      transform-gpu"
            style={{
              transform: 'perspective(1000px) rotateX(2deg)',
            }}
            layout
          >
            {/* Glass-like highlight effect at the top */}
            <div className="absolute top-0 left-0 right-0 h-px bg-white/20"></div>

            <form onSubmit={handleVerification} className="space-y-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaKey className="h-5 w-5 text-soft-pink/70" />
                </div>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  className="block w-full pl-10 pr-3 py-3 bg-pri-dark/50 border border-pri-gray/30 rounded-xl text-pri-white placeholder-pri-gray focus:ring-soft-pink focus:border-soft-pink"
                  placeholder="Enter verification code"
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-full shadow-sm text-pri-dark bg-mint-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-pink transition-colors duration-200 relative overflow-hidden"
              >
                Verify Email
              </motion.button>
            </form>

            {/* Error Message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-4 bg-red-500/10 text-red-500 p-3 rounded-lg text-sm"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Success Message */}
            <AnimatePresence>
              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-4 bg-green-500/10 text-green-400 p-3 rounded-lg text-sm"
                >
                  {success}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <div className="text-center mt-4">
            <button 
              onClick={() => navigate('/signup')} 
              className="text-sm text-soft-pink hover:text-mint-green transition-colors duration-300"
            >
              Back to Sign Up
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Verification;
